/**
 * Contexto del modo visual (claro/oscuro).
 *
 * La preferencia se guarda en `localStorage` —no en `sessionStorage`— porque es
 * una comodidad del puesto, no un dato de sesión: debe sobrevivir al cierre del
 * navegador. Si no hay preferencia guardada, se toma la del sistema operativo.
 *
 * El modo elegido aquí alimenta a los temas de MUI (`crearTemaAdmin`,
 * `crearTemaSalidas`) en `App`, de modo que toda la interfaz —acceso, salidas,
 * back-office y manual— comparte el mismo aspecto.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import type { ModoTema } from '../tema'

const CLAVE_ALMACEN = 'dox.tema'

interface ValorTema {
  modo: ModoTema
  alternar: () => void
  fijar: (modo: ModoTema) => void
}

const Contexto = createContext<ValorTema | null>(null)

function modoInicial(): ModoTema {
  try {
    const guardado = localStorage.getItem(CLAVE_ALMACEN)
    if (guardado === 'light' || guardado === 'dark') return guardado
  } catch {
    /* almacenamiento no disponible: se cae a la preferencia del sistema */
  }
  try {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
  } catch {
    /* sin matchMedia: por defecto claro */
  }
  return 'light'
}

export function ProveedorTema({ children }: { children: ReactNode }) {
  const [modo, setModo] = useState<ModoTema>(modoInicial)

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE_ALMACEN, modo)
    } catch {
      /* si no se puede guardar, el modo sigue vigente en memoria */
    }
    // Alinea el fondo de la página con el tema, para que no asome el blanco del
    // documento antes de que MUI pinte.
    document.documentElement.style.colorScheme = modo
  }, [modo])

  const alternar = useCallback(() => {
    setModo((actual) => (actual === 'dark' ? 'light' : 'dark'))
  }, [])

  const fijar = useCallback((nuevo: ModoTema) => setModo(nuevo), [])

  const valor = useMemo(() => ({ modo, alternar, fijar }), [modo, alternar, fijar])

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function useTema(): ValorTema {
  const valor = useContext(Contexto)
  if (!valor) throw new Error('useTema debe usarse dentro de ProveedorTema')
  return valor
}
